import { useState, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Card,
  Title2,
  Body1,
  Caption1,
  Field,
  Checkbox,
  Textarea,
  Button,
  Combobox,
  Option,
  Persona,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { CheckmarkCircleRegular, SendRegular } from '@fluentui/react-icons';
import { apiClient, ListResponse } from '../api/client';

const useStyles = makeStyles({
  container: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  header: {
    marginBottom: tokens.spacingVerticalXL,
  },
  card: {
    padding: tokens.spacingVerticalXL,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: tokens.spacingHorizontalL,
  },
  sectionLabel: {
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: tokens.spacingVerticalXS,
  },
  delegateField: {
    marginLeft: tokens.spacingHorizontalXL,
    marginTop: `-${tokens.spacingVerticalS}`,
  },
  hint: {
    display: 'block',
    color: tokens.colorNeutralForeground3,
    marginLeft: tokens.spacingHorizontalXL,
    marginTop: `-${tokens.spacingVerticalXS}`,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalL,
    paddingTop: tokens.spacingVerticalL,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  successCard: {
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
  },
  successIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: '#e6f4ea',
    color: '#137333',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
    marginBottom: tokens.spacingVerticalL,
    fontSize: '32px',
  },
});

interface GraphUser {
  id: string;
  displayName: string;
  mail: string;
  userPrincipalName: string;
  jobTitle?: string;
  department?: string;
}

interface FormData {
  userId: string;
  userPrincipalName: string;
  userDisplayName: string;
  userEmail: string;
  department: string;
  jobTitle: string;
  terminationDate: Date | null;
  delegateOneDriveAccess: boolean;
  oneDriveDelegate: string;
  oneDriveDelegateName: string;
  oneDriveDelegateEmail: string;
  delegateEmailAccess: boolean;
  emailDelegate: string;
  emailDelegateName: string;
  emailDelegateEmail: string;
  additionalNotes: string;
}

const initialFormData: FormData = {
  userId: '',
  userPrincipalName: '',
  userDisplayName: '',
  userEmail: '',
  department: '',
  jobTitle: '',
  terminationDate: null,
  delegateOneDriveAccess: false,
  oneDriveDelegate: '',
  oneDriveDelegateName: '',
  oneDriveDelegateEmail: '',
  delegateEmailAccess: false,
  emailDelegate: '',
  emailDelegateName: '',
  emailDelegateEmail: '',
  additionalNotes: '',
};

export function OffboardingPage() {
  const styles = useStyles();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [userQuery, setUserQuery] = useState('');
  const [users, setUsers] = useState<GraphUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [oneDriveQuery, setOneDriveQuery] = useState('');
  const [oneDriveDelegates, setOneDriveDelegates] = useState<GraphUser[]>([]);
  const [oneDriveLoading, setOneDriveLoading] = useState(false);
  const [emailQuery, setEmailQuery] = useState('');
  const [emailDelegates, setEmailDelegates] = useState<GraphUser[]>([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [requestId, setRequestId] = useState<string | null>(null);

  // Search users to offboard
  useEffect(() => {
    if (userQuery.length < 2) {
      setUsers([]);
      return;
    }

    const timer = setTimeout(async () => {
      setUsersLoading(true);
      try {
        const response = await apiClient.get<ListResponse<GraphUser>>(
          `/users/search?q=${encodeURIComponent(userQuery)}`
        );
        setUsers(response.data);
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setUsersLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [userQuery]);

  // Search OneDrive delegates
  useEffect(() => {
    if (oneDriveQuery.length < 2) {
      setOneDriveDelegates([]);
      return;
    }

    const timer = setTimeout(async () => {
      setOneDriveLoading(true);
      try {
        const response = await apiClient.get<ListResponse<GraphUser>>(
          `/users/search?q=${encodeURIComponent(oneDriveQuery)}`
        );
        setOneDriveDelegates(response.data);
      } catch (error) {
        console.error('Error searching delegates:', error);
      } finally {
        setOneDriveLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [oneDriveQuery]);

  // Search email delegates
  useEffect(() => {
    if (emailQuery.length < 2) {
      setEmailDelegates([]);
      return;
    }

    const timer = setTimeout(async () => {
      setEmailLoading(true);
      try {
        const response = await apiClient.get<ListResponse<GraphUser>>(
          `/users/search?q=${encodeURIComponent(emailQuery)}`
        );
        setEmailDelegates(response.data);
      } catch (error) {
        console.error('Error searching delegates:', error);
      } finally {
        setEmailLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [emailQuery]);

  const handleUserSelect = (user: GraphUser) => {
    setFormData({
      ...formData,
      userId: user.id,
      userPrincipalName: user.userPrincipalName,
      userDisplayName: user.displayName,
      userEmail: user.mail,
      department: user.department || '',
      jobTitle: user.jobTitle || '',
    });
    setUserQuery(user.displayName);
  };

  const handleOneDriveSelect = (user: GraphUser) => {
    setFormData({
      ...formData,
      oneDriveDelegate: user.id,
      oneDriveDelegateName: user.displayName,
      oneDriveDelegateEmail: user.mail,
    });
    setOneDriveQuery(user.displayName);
  };

  const handleEmailSelect = (user: GraphUser) => {
    setFormData({
      ...formData,
      emailDelegate: user.id,
      emailDelegateName: user.displayName,
      emailDelegateEmail: user.mail,
    });
    setEmailQuery(user.displayName);
  };

  const handleSubmit = async () => {
    if (!formData.userId || !formData.terminationDate) {
      setError('Please fill in all required fields');
      return;
    }
    if (formData.delegateOneDriveAccess && !formData.oneDriveDelegate) {
      setError("Please choose who should get access to the user's OneDrive files");
      return;
    }
    if (formData.delegateEmailAccess && !formData.emailDelegate) {
      setError("Please choose who should get access to the user's email");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await apiClient.post<{ data: { requestId: string } }>('/offboarding', {
        ...formData,
        terminationDate: formData.terminationDate.toISOString().split('T')[0],
      });

      setRequestId(response.data.requestId);
      setIsComplete(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setUserQuery('');
    setOneDriveQuery('');
    setEmailQuery('');
    setIsComplete(false);
    setRequestId(null);
  };

  if (isComplete) {
    return (
      <div className={styles.container}>
        <Card className={styles.successCard}>
          <div className={styles.successIcon}>
            <CheckmarkCircleRegular />
          </div>
          <Title2 style={{ marginBottom: tokens.spacingVerticalM }}>
            Offboarding Request Submitted!
          </Title2>
          <Body1 style={{ color: tokens.colorNeutralForeground3, marginBottom: tokens.spacingVerticalL }}>
            Your request is pending admin approval.
          </Body1>
          {requestId && (
            <MessageBar intent="info" style={{ marginBottom: tokens.spacingVerticalL }}>
              <MessageBarBody>
                <MessageBarTitle>Request ID: {requestId}</MessageBarTitle>
              </MessageBarBody>
            </MessageBar>
          )}
          <Button appearance="primary" onClick={handleReset}>
            Submit Another Request
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title2>Employee Offboarding</Title2>
        <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
          Initiate the offboarding process for a departing employee
        </Body1>
      </div>

      <Card className={styles.card}>
        {error && (
          <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalL }}>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        <div className={styles.form}>
          <Field label="Employee to Offboard" required>
            <Combobox
              value={userQuery || formData.userDisplayName}
              onChange={(e) => setUserQuery((e.target as HTMLInputElement).value)}
              placeholder="Search for an employee..."
              freeform
            >
              {usersLoading && (
                <Option value="" text="Searching..." disabled>
                  <Spinner size="tiny" /> Searching...
                </Option>
              )}
              {users.map((user) => (
                <Option
                  key={user.id}
                  value={user.id}
                  text={user.displayName}
                  onClick={() => handleUserSelect(user)}
                >
                  <Persona
                    name={user.displayName}
                    secondaryText={user.mail}
                    tertiaryText={user.jobTitle}
                    size="small"
                  />
                </Option>
              ))}
            </Combobox>
          </Field>

          {formData.userId && (
            <div className={styles.row}>
              <Field label="Department">
                <Body1>{formData.department || 'Not specified'}</Body1>
              </Field>
              <Field label="Job Title">
                <Body1>{formData.jobTitle || 'Not specified'}</Body1>
              </Field>
            </div>
          )}

          <Field
            label="Termination Date"
            required
            hint="The account will be automatically disabled and sign-in revoked on this date."
          >
            <DatePicker
              value={formData.terminationDate}
              onSelectDate={(date) => setFormData({ ...formData, terminationDate: date ?? null })}
              placeholder="Select termination date"
            />
          </Field>

          <div>
            <Body1 className={styles.sectionLabel}>Data &amp; access handling</Body1>
            <MessageBar intent="info">
              <MessageBarBody>
                On the termination date the account is automatically disabled. The delegations
                below are recorded and sent to IT to complete manually in the Microsoft 365 admin
                center (they cannot be automated).
              </MessageBarBody>
            </MessageBar>
          </div>

          <Checkbox
            checked={formData.delegateOneDriveAccess}
            onChange={(_, data) =>
              setFormData({
                ...formData,
                delegateOneDriveAccess: !!data.checked,
                ...(data.checked
                  ? {}
                  : { oneDriveDelegate: '', oneDriveDelegateName: '', oneDriveDelegateEmail: '' }),
              })
            }
            label="Give another user access to this user's OneDrive files for 30 days after the user is deleted"
          />
          {formData.delegateOneDriveAccess && (
            <div className={styles.delegateField}>
              <Field>
                <Combobox
                  value={oneDriveQuery || formData.oneDriveDelegateName}
                  onChange={(e) => setOneDriveQuery((e.target as HTMLInputElement).value)}
                  placeholder="Search for a user..."
                  freeform
                >
                  {oneDriveLoading && (
                    <Option value="" text="Searching..." disabled>
                      <Spinner size="tiny" /> Searching...
                    </Option>
                  )}
                  {oneDriveDelegates.map((user) => (
                    <Option
                      key={user.id}
                      value={user.id}
                      text={user.displayName}
                      onClick={() => handleOneDriveSelect(user)}
                    >
                      <Persona name={user.displayName} secondaryText={user.mail} size="small" />
                    </Option>
                  ))}
                </Combobox>
              </Field>
            </div>
          )}

          <Checkbox
            checked={formData.delegateEmailAccess}
            onChange={(_, data) =>
              setFormData({
                ...formData,
                delegateEmailAccess: !!data.checked,
                ...(data.checked
                  ? {}
                  : { emailDelegate: '', emailDelegateName: '', emailDelegateEmail: '' }),
              })
            }
            label="Give another user access to this user's email"
          />
          <Caption1 className={styles.hint}>
            The mailbox will be converted to a shared mailbox and email won't be deleted.
          </Caption1>
          {formData.delegateEmailAccess && (
            <div className={styles.delegateField}>
              <Field>
                <Combobox
                  value={emailQuery || formData.emailDelegateName}
                  onChange={(e) => setEmailQuery((e.target as HTMLInputElement).value)}
                  placeholder="Search for a user..."
                  freeform
                >
                  {emailLoading && (
                    <Option value="" text="Searching..." disabled>
                      <Spinner size="tiny" /> Searching...
                    </Option>
                  )}
                  {emailDelegates.map((user) => (
                    <Option
                      key={user.id}
                      value={user.id}
                      text={user.displayName}
                      onClick={() => handleEmailSelect(user)}
                    >
                      <Persona name={user.displayName} secondaryText={user.mail} size="small" />
                    </Option>
                  ))}
                </Combobox>
              </Field>
            </div>
          )}

          <Field label="Additional Notes">
            <Textarea
              value={formData.additionalNotes}
              onChange={(_, data) => setFormData({ ...formData, additionalNotes: data.value })}
              placeholder="Any additional information..."
              rows={4}
            />
          </Field>

          <div className={styles.actions}>
            <Button appearance="secondary" onClick={handleReset}>
              Clear
            </Button>
            <Button
              appearance="primary"
              icon={isSubmitting ? <Spinner size="tiny" /> : <SendRegular />}
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
